import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import {
  companyDetailsDefaultValues,
  INDUSTRY_SECTORS,
  type ExtendedCompanyDetailsFormValues,
} from '../schemas/company-details.schema';
import { useCompanyTypeStore } from '../../company-type/stores/company-type-store';

/**
 * Thin coordinator store for CompanyDetails.
 *
 * The full validation logic has moved to react-hook-form inside CompanyDetailsPage.tsx.
 * This store exists only to:
 *  - Expose isValid to use-step-validation.hook.ts (stepper gating)
 *  - Expose formData + getApiPayload() to use-onboarding-payload.hook.ts (API payload building)
 *  - Expose setFieldError / markFieldAsInteracted to use-onboarding-api.hook.ts (API error injection)
 *  - Expose resetForm() to store-manager.ts (clearing stores on company type change)
 */

interface CompanyDetailsState extends BaseStoreState {
  formData: Partial<ExtendedCompanyDetailsFormValues>;
  isValid: boolean;
  pendingApiError: { field: string; message: string } | null;
}

export interface CompanyDetailsStore extends CompanyDetailsState {
  setValidity: (_isValid: boolean) => void;
  setFormData: (_data: Partial<ExtendedCompanyDetailsFormValues>) => void;
  setFieldError: (_field: string, _error: string | null) => void;
  clearApiError: () => void;
  // No-op kept for use-onboarding-api.hook.ts compatibility
  markFieldAsInteracted: (_field: string) => void;
  resetForm: () => void;
  getApiPayload: () => Record<string, unknown>;
}

const initialFormData =
  companyDetailsDefaultValues as Partial<ExtendedCompanyDetailsFormValues>;

export const useCompanyDetailsStore = createPersistedStore<CompanyDetailsState>(
  'company-details',
  {
    formData: initialFormData,
    isValid: false,
    pendingApiError: null,
  },
  (set, get) => ({
    setValidity: (isValid: boolean) => set({ isValid }),

    setFormData: (data: Partial<ExtendedCompanyDetailsFormValues>) =>
      set((state: CompanyDetailsState) => ({
        formData: { ...state.formData, ...data },
      })),

    setFieldError: (field: string, error: string | null) =>
      set({ pendingApiError: error ? { field, message: error } : null }),

    clearApiError: () => set({ pendingApiError: null }),

    // No-op: interacted state is owned by react-hook-form; kept for use-onboarding-api compatibility
    markFieldAsInteracted: (_field: string) => {},

    resetForm: () =>
      set({ formData: initialFormData, isValid: false, pendingApiError: null }),

    getApiPayload: () => {
      const { formData } = get();
      const companyTypeData = useCompanyTypeStore.getState().formData;

      const findLabel = (
        options: Array<{ value: string; label: string }>,
        value: string
      ) => {
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : value;
      };

      const apiPayload: Record<string, unknown> = { ...formData };

      // Convert industry sector value to label and map to 'industry' for BFF
      if (apiPayload.industrySector) {
        apiPayload.industry = findLabel(
          INDUSTRY_SECTORS,
          apiPayload.industrySector as string
        );
        delete apiPayload.industrySector;
      }

      // Map countryOfHeadquarter to country for BFF API
      if (apiPayload.countryOfHeadquarter) {
        apiPayload.country = apiPayload.countryOfHeadquarter;
        delete apiPayload.countryOfHeadquarter;
      }

      // Handle company type mapping for BFF API
      let finalCompanyType = companyTypeData.companyType;
      let finalCompanyStatus: string | undefined =
        companyTypeData.companyStatus;

      // For PIF_ESTABLISHED, send only companyType without companyStatus
      if (companyTypeData.companyStatus === 'PIF_ESTABLISHED') {
        finalCompanyType = 'PIF_ESTABLISHED';
        finalCompanyStatus = undefined;
      }

      const result: Record<string, unknown> = {
        ...apiPayload,
        companyType: finalCompanyType,
      };

      if (finalCompanyStatus && finalCompanyStatus !== 'PIF_ESTABLISHED') {
        result.companyStatus = finalCompanyStatus;
      }

      return result;
    },
  }),
  {
    partialize: (state: CompanyDetailsState) => ({
      formData: state.formData,
      isValid: state.isValid,
      // pendingApiError intentionally excluded — transient, not persisted
    }),
  }
) as unknown as PersistedStoreHook<CompanyDetailsStore>;

// Selectors — interfaces with use-step-validation and use-onboarding-payload unchanged
export const useCompanyDetailsFormData = () =>
  useCompanyDetailsStore((state: CompanyDetailsState) => state.formData);
export const useCompanyDetailsErrors = () =>
  ({}) as Partial<Record<keyof ExtendedCompanyDetailsFormValues, string>>;
export const useCompanyDetailsIsValid = () =>
  useCompanyDetailsStore((state: CompanyDetailsState) => state.isValid);
export const useCompanyDetailsIsDirty = () => false;
export const useCompanyDetailsLoading = () => false;
export const useCompanyDetailsSubmit = () => async () => false;
export const useCompanyDetailsApiPayload = () =>
  useCompanyDetailsStore(
    (state: CompanyDetailsState) =>
      (state as unknown as CompanyDetailsStore).getApiPayload
  );
